export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  tenantId: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  avatarColor?: string;
  status: 'active' | 'inactive' | 'suspended';
  lastLogin?: string;
  title?: string;
  department?: string;
  location?: string;
  phone?: string;
  timezone?: string;
  bio?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: 'starter' | 'professional' | 'enterprise';
  userCount: number;
  maxUsers: number;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = 'super_admin' | 'administrator' | 'manager' | 'user' | 'tenant_admin';

export interface Permission {
  id: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'manage';
  description: string;
}

export interface RolePermissions {
  role: UserRole;
  permissions: Permission[];
}

export const ROLE_HIERARCHY: Record<UserRole, number> = {
  super_admin: 100,
  administrator: 80,
  tenant_admin: 60,
  manager: 40,
  user: 20,
};

export const DEFAULT_PERMISSIONS: Record<UserRole, Permission[]> = {
  super_admin: [
    { id: '1', resource: 'users', action: 'manage', description: 'Full user management' },
    { id: '2', resource: 'tenants', action: 'manage', description: 'Full tenant management' },
    { id: '3', resource: 'roles', action: 'manage', description: 'Role management' },
    { id: '4', resource: 'system', action: 'manage', description: 'System administration' },
    { id: '5', resource: 'reports', action: 'manage', description: 'All reports access' },
    { id: '6', resource: 'analytics', action: 'manage', description: 'All analytics access' },
    { id: '7', resource: 'billing', action: 'manage', description: 'Billing management' },
    { id: '8', resource: 'audit', action: 'manage', description: 'Audit logs access' },
    // CRM Permissions
    { id: '9', resource: 'customers', action: 'manage', description: 'Full customer management' },
    { id: '10', resource: 'leads', action: 'manage', description: 'Full lead management' },
    { id: '11', resource: 'contacts', action: 'manage', description: 'Full contact management' },
    { id: '12', resource: 'deals', action: 'manage', description: 'Full deal management' },
    { id: '13', resource: 'activities', action: 'manage', description: 'Full activity management' },
    { id: '14', resource: 'notes', action: 'manage', description: 'Full notes management' },
    { id: '15', resource: 'crm_analytics', action: 'manage', description: 'Full CRM analytics access' },
  ],
  administrator: [
    { id: '9', resource: 'users', action: 'manage', description: 'User management within tenant' },
    { id: '10', resource: 'tenants', action: 'read', description: 'View tenant information' },
    { id: '11', resource: 'tenants', action: 'update', description: 'Update tenant settings' },
    { id: '12', resource: 'reports', action: 'manage', description: 'Reports access' },
    { id: '13', resource: 'analytics', action: 'read', description: 'Analytics viewing' },
    { id: '14', resource: 'audit', action: 'read', description: 'View audit logs' },
    // CRM Permissions
    { id: '15', resource: 'customers', action: 'manage', description: 'Full customer management' },
    { id: '16', resource: 'leads', action: 'manage', description: 'Full lead management' },
    { id: '17', resource: 'contacts', action: 'manage', description: 'Full contact management' },
    { id: '18', resource: 'deals', action: 'manage', description: 'Full deal management' },
    { id: '19', resource: 'activities', action: 'manage', description: 'Full activity management' },
    { id: '20', resource: 'notes', action: 'manage', description: 'Full notes management' },
    { id: '21', resource: 'crm_analytics', action: 'manage', description: 'Full CRM analytics access' },
  ],
  tenant_admin: [
    { id: '15', resource: 'users', action: 'manage', description: 'Manage tenant users' },
    { id: '16', resource: 'tenants', action: 'read', description: 'View tenant information' },
    { id: '17', resource: 'reports', action: 'read', description: 'View tenant reports' },
    { id: '18', resource: 'analytics', action: 'read', description: 'View tenant analytics' },
    // CRM Permissions
    { id: '19', resource: 'customers', action: 'manage', description: 'Full customer management' },
    { id: '20', resource: 'leads', action: 'manage', description: 'Full lead management' },
    { id: '21', resource: 'contacts', action: 'manage', description: 'Full contact management' },
    { id: '22', resource: 'deals', action: 'manage', description: 'Full deal management' },
    { id: '23', resource: 'activities', action: 'manage', description: 'Full activity management' },
    { id: '24', resource: 'notes', action: 'manage', description: 'Full notes management' },
    { id: '25', resource: 'crm_analytics', action: 'read', description: 'View CRM analytics' },
  ],
  manager: [
    { id: '19', resource: 'users', action: 'read', description: 'View team users' },
    { id: '20', resource: 'users', action: 'update', description: 'Update team member info' },
    { id: '21', resource: 'reports', action: 'read', description: 'View team reports' },
    { id: '22', resource: 'analytics', action: 'read', description: 'View team analytics' },
    // CRM Permissions
    { id: '23', resource: 'customers', action: 'read', description: 'View customers' },
    { id: '24', resource: 'customers', action: 'update', description: 'Update customer information' },
    { id: '25', resource: 'leads', action: 'manage', description: 'Full lead management' },
    { id: '26', resource: 'contacts', action: 'manage', description: 'Full contact management' },
    { id: '27', resource: 'deals', action: 'manage', description: 'Full deal management' },
    { id: '28', resource: 'activities', action: 'manage', description: 'Full activity management' },
    { id: '29', resource: 'notes', action: 'manage', description: 'Full notes management' },
    { id: '30', resource: 'crm_analytics', action: 'read', description: 'View CRM analytics' },
  ],
  user: [
    { id: '23', resource: 'profile', action: 'read', description: 'View own profile' },
    { id: '24', resource: 'profile', action: 'update', description: 'Update own profile' },
    { id: '25', resource: 'reports', action: 'read', description: 'View personal reports' },
    // CRM Permissions
    { id: '26', resource: 'customers', action: 'read', description: 'View assigned customers' },
    { id: '27', resource: 'leads', action: 'read', description: 'View assigned leads' },
    { id: '28', resource: 'leads', action: 'update', description: 'Update assigned leads' },
    { id: '29', resource: 'contacts', action: 'read', description: 'View contacts' },
    { id: '30', resource: 'contacts', action: 'update', description: 'Update contact information' },
    { id: '31', resource: 'deals', action: 'read', description: 'View assigned deals' },
    { id: '32', resource: 'deals', action: 'update', description: 'Update assigned deals' },
    { id: '33', resource: 'activities', action: 'read', description: 'View assigned activities' },
    { id: '34', resource: 'activities', action: 'update', description: 'Update assigned activities' },
    { id: '35', resource: 'activities', action: 'create', description: 'Create new activities' },
    { id: '36', resource: 'notes', action: 'read', description: 'View notes' },
    { id: '37', resource: 'notes', action: 'create', description: 'Create notes' },
    { id: '38', resource: 'notes', action: 'update', description: 'Update own notes' },
  ],
};

// CRM Entity Types
export interface Customer {
  id: string;
  tenantId: string;
  companyName: string;
  industry?: string;
  website?: string;
  description?: string;
  status: 'active' | 'inactive' | 'prospect' | 'former';
  annualRevenue?: number;
  employeeCount?: number;
  address?: Address;
  primaryContactId?: string;
  tags: string[];
  customFields: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastActivityAt?: string;
}

export interface Lead {
  id: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  leadSource: 'website' | 'referral' | 'cold_outreach' | 'advertising' | 'social_media' | 'trade_show' | 'other';
  leadStatus: 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  leadScore: number;
  estimatedValue?: number;
  expectedCloseDate?: string;
  assignedTo?: string;
  tags: string[];
  customFields: Record<string, any>;
  notes: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastActivityAt?: string;
}

export interface Contact {
  id: string;
  tenantId: string;
  customerId?: string;
  leadId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  jobTitle?: string;
  department?: string;
  address?: Address;
  socialProfiles: SocialProfile[];
  tags: string[];
  customFields: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastActivityAt?: string;
}

export interface Deal {
  id: string;
  tenantId: string;
  customerId: string;
  contactId: string;
  title: string;
  description?: string;
  value: number;
  currency: string;
  stage: DealStage;
  probability: number;
  expectedCloseDate?: string;
  actualCloseDate?: string;
  assignedTo: string;
  tags: string[];
  customFields: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastActivityAt?: string;
}

export interface Activity {
  id: string;
  tenantId: string;
  customerId?: string;
  contactId?: string;
  leadId?: string;
  dealId?: string;
  type: ActivityType;
  title: string;
  description?: string;
  scheduledAt?: string;
  completedAt?: string;
  duration?: number; // in minutes
  assignedTo: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'overdue';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  location?: string;
  participants: string[]; // user IDs
  tags: string[];
  customFields: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Note {
  id: string;
  tenantId: string;
  customerId?: string;
  contactId?: string;
  leadId?: string;
  dealId?: string;
  activityId?: string;
  title?: string;
  content: string;
  isPrivate: boolean;
  tags: string[];
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// Supporting Types
export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface SocialProfile {
  platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'other';
  url: string;
  username?: string;
}

export interface Attachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  uploadedAt: string;
  uploadedBy: string;
}

export type DealStage =
  | 'prospecting'
  | 'qualification'
  | 'proposal'
  | 'negotiation'
  | 'closed_won'
  | 'closed_lost';

export type ActivityType =
  | 'call'
  | 'meeting'
  | 'email'
  | 'demo'
  | 'task'
  | 'follow_up'
  | 'site_visit'
  | 'other';

// CRM Analytics Types
export interface CrmAnalytics {
  overview: {
    totalCustomers: number;
    activeCustomers: number;
    totalLeads: number;
    convertedLeads: number;
    totalDeals: number;
    wonDeals: number;
    totalRevenue: number;
    averageDealSize: number;
  };
  pipeline: {
    stages: Array<{
      stage: DealStage;
      count: number;
      value: number;
      averageProbability: number;
    }>;
    forecastRevenue: number;
    pipelineVelocity: number;
  };
  salesPerformance: {
    teamMetrics: Array<{
      userId: string;
      userName: string;
      dealsWon: number;
      revenueGenerated: number;
      conversionRate: number;
    }>;
    monthlyTrends: Array<{
      month: string;
      leadsGenerated: number;
      dealsClosed: number;
      revenue: number;
    }>;
  };
  customerInsights: {
    acquisitionChannels: Array<{
      channel: string;
      count: number;
      conversionRate: number;
    }>;
    customerLifetimeValue: number;
    churnRate: number;
    retentionRate: number;
  };
}

// Filter and Search Types
export interface CrmFilter {
  dateRange?: {
    start: string;
    end: string;
  };
  assignedTo?: string[];
  status?: string[];
  tags?: string[];
  customFields?: Record<string, any>;
  searchQuery?: string;
}

export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PaginationOptions {
  page: number;
  pageSize: number;
  total?: number;
}

// Bulk Operations Types
export interface BulkOperation<T = any> {
  operation: 'update' | 'delete' | 'export' | 'assign';
  ids: string[];
  data?: Partial<T>;
  newAssignee?: string;
}

export interface BulkOperationResult {
  success: boolean;
  processedCount: number;
  failedCount: number;
  errors?: string[];
}

// CRM Service Types
export interface CrmApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CrmService {
  // Customer operations
  getCustomers: (filters?: CrmFilter, pagination?: PaginationOptions, sort?: SortOption) => Promise<CrmApiResponse<Customer[]>>;
  getCustomer: (id: string) => Promise<CrmApiResponse<Customer>>;
  createCustomer: (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Customer>>;
  updateCustomer: (id: string, customer: Partial<Customer>) => Promise<CrmApiResponse<Customer>>;
  deleteCustomer: (id: string) => Promise<CrmApiResponse<void>>;
  bulkCustomerOperation: (operation: BulkOperation<Customer>) => Promise<CrmApiResponse<BulkOperationResult>>;

  // Lead operations
  getLeads: (filters?: CrmFilter, pagination?: PaginationOptions, sort?: SortOption) => Promise<CrmApiResponse<Lead[]>>;
  getLead: (id: string) => Promise<CrmApiResponse<Lead>>;
  createLead: (lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Lead>>;
  updateLead: (id: string, lead: Partial<Lead>) => Promise<CrmApiResponse<Lead>>;
  deleteLead: (id: string) => Promise<CrmApiResponse<void>>;
  convertLead: (id: string, customerData: Partial<Customer>) => Promise<CrmApiResponse<Customer>>;
  bulkLeadOperation: (operation: BulkOperation<Lead>) => Promise<CrmApiResponse<BulkOperationResult>>;

  // Contact operations
  getContacts: (filters?: CrmFilter, pagination?: PaginationOptions, sort?: SortOption) => Promise<CrmApiResponse<Contact[]>>;
  getContact: (id: string) => Promise<CrmApiResponse<Contact>>;
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Contact>>;
  updateContact: (id: string, contact: Partial<Contact>) => Promise<CrmApiResponse<Contact>>;
  deleteContact: (id: string) => Promise<CrmApiResponse<void>>;
  bulkContactOperation: (operation: BulkOperation<Contact>) => Promise<CrmApiResponse<BulkOperationResult>>;

  // Deal operations
  getDeals: (filters?: CrmFilter, pagination?: PaginationOptions, sort?: SortOption) => Promise<CrmApiResponse<Deal[]>>;
  getDeal: (id: string) => Promise<CrmApiResponse<Deal>>;
  createDeal: (deal: Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Deal>>;
  updateDeal: (id: string, deal: Partial<Deal>) => Promise<CrmApiResponse<Deal>>;
  deleteDeal: (id: string) => Promise<CrmApiResponse<void>>;
  moveDealStage: (id: string, newStage: DealStage, probability?: number) => Promise<CrmApiResponse<Deal>>;
  bulkDealOperation: (operation: BulkOperation<Deal>) => Promise<CrmApiResponse<BulkOperationResult>>;

  // Activity operations
  getActivities: (filters?: CrmFilter, pagination?: PaginationOptions, sort?: SortOption) => Promise<CrmApiResponse<Activity[]>>;
  getActivity: (id: string) => Promise<CrmApiResponse<Activity>>;
  createActivity: (activity: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Activity>>;
  updateActivity: (id: string, activity: Partial<Activity>) => Promise<CrmApiResponse<Activity>>;
  completeActivity: (id: string, completedAt?: string) => Promise<CrmApiResponse<Activity>>;
  deleteActivity: (id: string) => Promise<CrmApiResponse<void>>;
  bulkActivityOperation: (operation: BulkOperation<Activity>) => Promise<CrmApiResponse<BulkOperationResult>>;

  // Note operations
  getNotes: (entityId: string, entityType: 'customer' | 'contact' | 'lead' | 'deal' | 'activity') => Promise<CrmApiResponse<Note[]>>;
  createNote: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CrmApiResponse<Note>>;
  updateNote: (id: string, note: Partial<Note>) => Promise<CrmApiResponse<Note>>;
  deleteNote: (id: string) => Promise<CrmApiResponse<void>>;

  // Analytics
  getCrmAnalytics: (dateRange?: { start: string; end: string }) => Promise<CrmApiResponse<CrmAnalytics>>;

  // Search
  searchCrmEntities: (query: string, entityTypes?: string[], filters?: CrmFilter) => Promise<CrmApiResponse<{
    customers: Customer[];
    leads: Lead[];
    contacts: Contact[];
    deals: Deal[];
  }>>;
}

// Tenant roster and assignment types
export interface TenantUserAssignment {
  tenantId: string;
  userId: string;
  role: UserRole;
  status: 'active' | 'invited' | 'suspended';
  lastActive: string;
  assignedAt: string;
  assignedBy: string;
  updatedAt?: string;
}

export interface TenantRosterEntry {
  user: User;
  assignment: TenantUserAssignment;
}

export interface TenantRoster {
  tenant: Tenant;
  roster: TenantRosterEntry[];
  unassignedUsers: User[];
  roleDistribution: Array<{
    role: UserRole;
    count: number;
  }>;
}

export interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  permissions: Permission[];
  hasPermission: (resource: string, action: Permission['action']) => boolean;
  hasAnyPermission: (permissions: Array<{resource: string, action: Permission['action']}>) => boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}
